import * as Sentry from '@sentry/react';
import type { DriverState } from '@playcastdotio/CoreTypes';
import type { DeviceConfigManager } from './DeviceConfigManager';
import { resolveShadowcamDevice, getPluggedShadowcamSlotKeys } from './ShadowcamCaptureResolver';
import {
  getUserMediaForShadowCam,
  SHADOWCAM_AUDIO_CONSTRAINTS,
} from './sessionCastGetUserMedia';

const SHADOWCAM_DIAG = '[BLUE-STREAM-12010:SessionCast]';

type DriverStateLike = DriverState | Record<string, boolean> | undefined;

export type SessionCastBinding = {
  shadowCamName?: string;
  audioDeviceName?: string;
};

export type SessionAudioStatus =
  | { status: 'ready'; track: MediaStreamTrack }
  | { status: 'DRIVER_BLOCKED'; track: null; reason: string };

export type SessionCaptureReadiness =
  | { status: 'ready' }
  | { status: 'DRIVER_BLOCKED'; reason: string };

export type SessionCastResult = {
  videoTrack: MediaStreamTrack | null;
  audio: SessionAudioStatus;
  readiness: SessionCaptureReadiness;
};

type ShadowCamOpenResult = {
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
};

export class SessionCastManager {
  private readonly videoTracks = new Map<string, MediaStreamTrack>();
  private readonly audioStatuses = new Map<string, SessionAudioStatus>();

  constructor(
    private readonly deviceConfig: DeviceConfigManager,
    private readonly getDriverState: () => DriverStateLike,
  ) {}

  public getVideoTrackForSession(sessionId: string): MediaStreamTrack | null {
    return this.getLiveTrack(this.videoTracks.get(sessionId));
  }

  public getAudioTrackForSession(sessionId: string): MediaStreamTrack | null {
    const status = this.audioStatuses.get(sessionId);
    if (status?.status !== 'ready') return null;
    return this.getLiveTrack(status.track);
  }

  public getAudioStatusForSession(sessionId: string): SessionAudioStatus | undefined {
    return this.audioStatuses.get(sessionId);
  }

  public async startCastForSession(
    sessionId: string,
    binding: SessionCastBinding,
  ): Promise<SessionCastResult> {
    Sentry.logger.info(
      `${SHADOWCAM_DIAG} startCast session=${sessionId} ` +
      `shadowCam=${binding.shadowCamName ?? 'none'} ` +
      `audioDevice=${binding.audioDeviceName ?? 'none'}`,
    );

    let videoTrack: MediaStreamTrack | null = null;
    let shadowCamAudio: MediaStreamTrack | null = null;

    if (binding.shadowCamName) {
      // When audioDeviceName is set, open video-only and prefer AudioCaster by
      // label (silent ShadowCam pins must not win permanently). If that label
      // miss/fails, resolveSessionAudio reopens A+V as a secondary path.
      const opened = await this.openShadowCamForSession(sessionId, binding.shadowCamName, {
        withAudio: !binding.audioDeviceName,
      });
      videoTrack = opened.videoTrack;
      shadowCamAudio = opened.audioTrack;
    } else {
      videoTrack = this.getVideoTrackForSession(sessionId);
    }

    const audio = await this.resolveSessionAudio(
      sessionId,
      binding.audioDeviceName,
      shadowCamAudio,
      binding.shadowCamName,
    );

    return {
      videoTrack,
      audio,
      readiness: videoTrack
        ? { status: 'ready' }
        : {
            status: 'DRIVER_BLOCKED',
            reason: binding.shadowCamName
              ? `ShadowCam "${binding.shadowCamName}" is unavailable for session ${sessionId}`
              : `No ShadowCam is configured for session ${sessionId}`,
          },
    };
  }

  public async startVideoForSession(
    sessionId: string,
    hubRoute: string,
  ): Promise<MediaStreamTrack | null> {
    const { videoTrack } = await this.openShadowCamForSession(sessionId, hubRoute, {
      withAudio: false,
    });
    return videoTrack;
  }

  public async startAudioForSession(
    sessionId: string,
    audioDeviceName: string,
  ): Promise<SessionAudioStatus> {
    const existing = this.getAudioTrackForSession(sessionId);
    if (existing) return { status: 'ready', track: existing };
    if (!navigator.mediaDevices?.enumerateDevices || !navigator.mediaDevices?.getUserMedia) {
      return this.reportBlockedAudio(
        sessionId,
        `navigator.mediaDevices unavailable for AudioCaster endpoint "${audioDeviceName}"`,
      );
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((device) => device.kind === 'audioinput');
    const audioLabels = audioInputs.map((device) => device.label || '(empty-label)');
    const target = audioInputs.find((device) => device.label === audioDeviceName);
    if (!target) {
      Sentry.logger.warn(
        `${SHADOWCAM_DIAG} AudioCaster label miss session=${sessionId} ` +
        `wanted="${audioDeviceName}" enumeratedAudioInputs=[${audioLabels.join(', ')}] ` +
        `count=${audioLabels.length}`,
      );
      Sentry.captureMessage(
        `${SHADOWCAM_DIAG} AudioCaster endpoint label not enumerated`,
        {
          level: 'error',
          tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'audiocaster_label' },
          fingerprint: ['audiocaster-label-miss', audioDeviceName],
          extra: {
            sessionId,
            wantedLabel: audioDeviceName,
            enumeratedAudioInputs: audioLabels,
            enumeratedAudioInputCount: audioLabels.length,
          },
        },
      );
      return this.reportBlockedAudio(
        sessionId,
        `Explicit AudioCaster endpoint "${audioDeviceName}" is unavailable ` +
        `(enumerated ${audioLabels.length} audioinput(s))`,
      );
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: target.deviceId },
          ...SHADOWCAM_AUDIO_CONSTRAINTS,
        },
      });
      const track = stream.getAudioTracks()[0];
      if (!track) {
        return this.reportBlockedAudio(
          sessionId,
          `Explicit AudioCaster endpoint "${audioDeviceName}" produced no track`,
        );
      }
      Sentry.logger.info(
        `${SHADOWCAM_DIAG} AudioCaster ready session=${sessionId} label="${audioDeviceName}"`,
      );
      return this.rememberAudioReady(sessionId, track);
    } catch (error) {
      const name = (error as DOMException)?.name ?? 'Error';
      const message = error instanceof Error ? error.message : String(error);
      Sentry.captureException(error, {
        tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'audiocaster_get_user_media' },
        fingerprint: ['audiocaster-getusermedia-throw', audioDeviceName],
        extra: { sessionId, wantedLabel: audioDeviceName, enumeratedAudioInputs: audioLabels },
      });
      return this.reportBlockedAudio(
        sessionId,
        `Explicit AudioCaster endpoint "${audioDeviceName}" getUserMedia failed: ${name}: ${message}`,
      );
    }
  }

  public stopCastForSession(sessionId: string): void {
    this.videoTracks.get(sessionId)?.stop();
    const audio = this.audioStatuses.get(sessionId);
    if (audio?.status === 'ready') audio.track.stop();
    this.videoTracks.delete(sessionId);
    this.audioStatuses.delete(sessionId);
  }

  public stopAllSessions(): void {
    for (const sessionId of new Set([
      ...this.videoTracks.keys(),
      ...this.audioStatuses.keys(),
    ])) {
      this.stopCastForSession(sessionId);
    }
  }

  private async resolveSessionAudio(
    sessionId: string,
    audioDeviceName: string | undefined,
    shadowCamAudio: MediaStreamTrack | null,
    shadowCamName?: string,
  ): Promise<SessionAudioStatus> {
    // Prefer AudioCaster when a label is present (must-pass path). A live ShadowCam
    // pin that produces silence must not win permanently over that path.
    if (audioDeviceName) {
      if (shadowCamAudio) shadowCamAudio.stop();
      const audioCaster = await this.startAudioForSession(sessionId, audioDeviceName);
      if (audioCaster.status === 'ready') return audioCaster;

      // Label miss / AudioCaster unavailable: reopen ShadowCam A+V before staying
      // DRIVER_BLOCKED so players are not left video-only when the mic enum fails.
      if (shadowCamName) {
        Sentry.logger.warn(
          `${SHADOWCAM_DIAG} AudioCaster "${audioDeviceName}" unavailable for session=${sessionId}; ` +
          `retrying ShadowCam A+V on "${shadowCamName}" reason=${audioCaster.reason}`,
        );
        const opened = await this.openShadowCamForSession(sessionId, shadowCamName, {
          withAudio: true,
        });
        if (opened.audioTrack) {
          Sentry.logger.info(
            `${SHADOWCAM_DIAG} ShadowCam A+V fallback audio ready session=${sessionId} ` +
            `hubRoute=${shadowCamName} label="${opened.audioTrack.label}"`,
          );
          return this.rememberAudioReady(sessionId, opened.audioTrack);
        }
        Sentry.logger.warn(
          `${SHADOWCAM_DIAG} ShadowCam A+V fallback produced no audio track ` +
          `session=${sessionId} hubRoute=${shadowCamName}`,
        );
      }
      return audioCaster;
    }
    if (shadowCamAudio) {
      return this.rememberAudioReady(sessionId, shadowCamAudio);
    }
    return this.reportBlockedAudio(sessionId);
  }

  private async openShadowCamForSession(
    sessionId: string,
    hubRoute: string,
    options: { withAudio: boolean },
  ): Promise<ShadowCamOpenResult> {
    Sentry.logger.info(
      `${SHADOWCAM_DIAG} openShadowCam session=${sessionId} hubRoute=${hubRoute} withAudio=${options.withAudio}`,
    );
    Sentry.addBreadcrumb({
      category: 'BLUE-STREAM-12010',
      message: 'openShadowCam requested',
      level: 'info',
      data: { sessionId, hubRoute, withAudio: options.withAudio },
    });

    const existingVideo = this.getVideoTrackForSession(sessionId);
    const existingAudio = this.getAudioTrackForSession(sessionId);
    if (existingVideo && (!options.withAudio || existingAudio)) {
      Sentry.logger.info(
        `${SHADOWCAM_DIAG} reusing existing live track(s) for session=${sessionId} hubRoute=${hubRoute}`,
      );
      return { videoTrack: existingVideo, audioTrack: existingAudio };
    }

    // Video-only reopen for A+V: stop the prior video track first so the device is
    // not held busy / leaked when getUserMedia opens a replacement stream.
    if (existingVideo && options.withAudio && !existingAudio) {
      existingVideo.stop();
      this.videoTracks.delete(sessionId);
    }

    await this.deviceConfig.enumerateDevices();

    const shadowcams = this.deviceConfig.getShadowcams();
    const availableLabels = shadowcams.map((sc) => sc.displayName);
    const pluggedSlots = getPluggedShadowcamSlotKeys(this.getDriverState());

    const target = resolveShadowcamDevice(hubRoute, shadowcams, this.getDriverState());
    if (!target) {
      console.warn(
        `${SHADOWCAM_DIAG} resolveShadowcamDevice MISS session=${sessionId} hubRoute=${hubRoute} ` +
        `enumerated=[${availableLabels.join(', ')}] plugged=[${pluggedSlots.join(', ')}]`,
      );
      Sentry.captureMessage(
        `${SHADOWCAM_DIAG} ShadowCam "${hubRoute}" did not resolve to an enumerated device`,
        {
          level: 'error',
          tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'resolve_device' },
          fingerprint: ['shadowcam-resolve-miss', hubRoute],
          extra: {
            sessionId,
            hubRoute,
            enumeratedShadowcams: availableLabels,
            enumeratedCount: shadowcams.length,
            pluggedSlots,
            pluggedCount: pluggedSlots.length,
            likelyReason:
              shadowcams.length === 0
                ? 'no shadowcams enumerated (stale Chromium cache or permission gap)'
                : pluggedSlots.length > 1
                  ? 'multiple slots plugged and no enumerated label matched hubRoute'
                  : 'hubRoute not found among enumerated labels',
          },
        },
      );
      return { videoTrack: null, audioTrack: null };
    }

    Sentry.logger.info(
      `${SHADOWCAM_DIAG} resolved hubRoute=${hubRoute} -> device="${target.displayName}" ` +
      `deviceId=${target.deviceId ? target.deviceId.slice(0, 8) : 'no-id'} — calling getUserMedia`,
    );

    const stream = await getUserMediaForShadowCam(sessionId, hubRoute, target, options.withAudio, {
      availableLabels,
      pluggedSlots,
    });

    const videoTrack = stream.getVideoTracks()[0] ?? null;
    let audioTrack: MediaStreamTrack | null = null;

    if (options.withAudio) {
      // Chromium often attaches the default mic when `audio: true` has no groupId
      // match. Only keep audio that clearly belongs to this ShadowCam; otherwise
      // stop the stray track so resolveSessionAudio can fall back to AudioCaster.
      const candidate = stream.getAudioTracks()[0] ?? null;
      if (candidate && this.isShadowCamBoundAudio(candidate, videoTrack, target.displayName)) {
        audioTrack = candidate;
      } else {
        for (const track of stream.getAudioTracks()) {
          track.stop();
        }
        if (candidate) {
          console.warn(
            `${SHADOWCAM_DIAG} rejecting non-ShadowCam audio track label="${candidate.label}" ` +
            `for hubRoute=${hubRoute}; will fall back to audioDeviceName if present`,
          );
        }
      }
    }

    if (videoTrack) {
      Sentry.logger.info(
        `${SHADOWCAM_DIAG} getUserMedia SUCCESS session=${sessionId} hubRoute=${hubRoute} ` +
        `video(label="${videoTrack.label}" readyState=${videoTrack.readyState}) ` +
        `audio=${audioTrack ? `"${audioTrack.label}"` : 'none'}`,
      );
      this.rememberTrack(this.videoTracks, sessionId, videoTrack);
    } else {
      console.warn(
        `${SHADOWCAM_DIAG} getUserMedia returned a stream with NO video track session=${sessionId} hubRoute=${hubRoute}`,
      );
      Sentry.captureMessage(
        `${SHADOWCAM_DIAG} getUserMedia returned no video track for "${hubRoute}"`,
        {
          level: 'error',
          tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'get_user_media' },
          fingerprint: ['shadowcam-getusermedia-no-track', hubRoute],
          extra: { sessionId, hubRoute, resolvedDevice: target.displayName },
        },
      );
    }

    return { videoTrack, audioTrack };
  }

  /**
   * True when an A+V audio track is from the same ShadowCam as the video device,
   * not Chromium's default mic. Prefer groupId match with the video track; also
   * accept a label that includes the ShadowCam display name.
   */
  private isShadowCamBoundAudio(
    audioTrack: MediaStreamTrack,
    videoTrack: MediaStreamTrack | null,
    shadowCamDisplayName: string,
  ): boolean {
    const audioGroupId = audioTrack.getSettings?.().groupId;
    const videoGroupId = videoTrack?.getSettings?.().groupId;
    if (audioGroupId && videoGroupId && audioGroupId === videoGroupId) {
      return true;
    }
    const label = audioTrack.label ?? '';
    if (shadowCamDisplayName && label.includes(shadowCamDisplayName)) {
      return true;
    }
    return false;
  }

  private reportBlockedAudio(sessionId: string, reason?: string): SessionAudioStatus {
    const status: SessionAudioStatus = {
      status: 'DRIVER_BLOCKED',
      track: null,
      reason: reason ?? `No explicit per-session AudioCaster endpoint for ${sessionId}`,
    };
    this.audioStatuses.set(sessionId, status);
    Sentry.logger.warn(`${SHADOWCAM_DIAG} audio DRIVER_BLOCKED session=${sessionId} reason=${status.reason}`);
    return status;
  }

  private rememberAudioReady(sessionId: string, track: MediaStreamTrack): SessionAudioStatus {
    const status: SessionAudioStatus = { status: 'ready', track };
    this.audioStatuses.set(sessionId, status);
    track.addEventListener('ended', () => {
      if (this.getAudioTrackForSession(sessionId) === track) {
        this.audioStatuses.delete(sessionId);
      }
    });
    return status;
  }

  private rememberTrack(
    tracks: Map<string, MediaStreamTrack>,
    sessionId: string,
    track: MediaStreamTrack,
  ): void {
    tracks.set(sessionId, track);
    track.addEventListener('ended', () => {
      if (tracks.get(sessionId) === track) tracks.delete(sessionId);
    });
  }

  private getLiveTrack(track: MediaStreamTrack | undefined | null): MediaStreamTrack | null {
    return track && track.readyState !== 'ended' ? track : null;
  }
}
